import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { Field } from '@/console/ui/Field'
import { Stack } from '@/console/ui/Stack'
import { GlobalRollbackFlow, ROLLBACK_REQUEST_KIND, RollbackInFlight, RollbackRequestKind } from './GlobalRollbackFlow'
import {
  MAX_INBOUND_DOCUMENT_NUMBER_LENGTH,
  MAX_INBOUND_DOCUMENT_NUMBERS,
  ParsedInboundDocumentNumbers,
  estimatedMaxSeconds,
  isJudgeableInput,
} from './globalRollback'
import styles from './GlobalRollback.module.css'

const IN_FLIGHT_LABEL = {
  [ROLLBACK_REQUEST_KIND.JUDGING]: '판정',
  [ROLLBACK_REQUEST_KIND.EXECUTING]: '롤백 실행',
} satisfies Record<RollbackRequestKind, string>

/** 문서 수에 비례해 순차 처리돼(문서당 약 1초) 진행 표시가 없으면 멈춘 줄 안다(§6.6). */
export const GlobalRollbackProgress = ({ inFlight }: { inFlight: RollbackInFlight | null }): JSX.Element | null => {
  if (!inFlight) {
    return null
  }

  return (
    <Callout>
      <div className={styles.progress}>
        <span>
          {IN_FLIGHT_LABEL[inFlight.kind]} 중… {inFlight.documentCount}건 — 최대 약 {estimatedMaxSeconds(inFlight.documentCount)}초 걸릴 수 있어요.
        </span>
        <progress aria-label={`${IN_FLIGHT_LABEL[inFlight.kind]} 진행 중`} />
      </div>
    </Callout>
  )
}

const InputProblems = ({ parsed }: { parsed: ParsedInboundDocumentNumbers }): JSX.Element => (
  <>
    {parsed.overLimit ? <Callout tone="crit">중복을 뺀 뒤 최대 {MAX_INBOUND_DOCUMENT_NUMBERS}건까지 판정할 수 있어요.</Callout> : null}
    {parsed.tooLong.length > 0 ? (
      <Callout tone="crit">
        {MAX_INBOUND_DOCUMENT_NUMBER_LENGTH}자를 넘는 문서번호가 있어요: {parsed.tooLong.join(', ')}
      </Callout>
    ) : null}
  </>
)

/** 유효하지 않은 값이 섞이면 판정을 막는다 — 일부만 판정하면 담당자는 전부 확인했다고 믿게 된다. */
export const GlobalRollbackInput = ({ flow }: { flow: GlobalRollbackFlow }): JSX.Element => {
  const { parsed, requestInFlight } = flow
  const mwmsSkipCount = flow.dryRunRequest.mwmsAlreadyCanceledDocumentNumbers.length

  return (
    <Stack>
      <Field label="입고문서번호 (한 줄에 하나)">
        <textarea
          rows={5}
          placeholder="2026092317-MUSINSAGLOBAL1"
          spellCheck={false}
          value={flow.documentNumbersText}
          disabled={requestInFlight}
          onChange={(event) => flow.setDocumentNumbersText(event.target.value)}
        />
      </Field>
      <div className={styles.meta}>
        유효 {parsed.inboundDocumentNumbers.length}건 · 중복 제외 {parsed.duplicates}건 · 최대 {MAX_INBOUND_DOCUMENT_NUMBERS}건
        {mwmsSkipCount > 0 ? ` · mWMS 취소 확인 ${mwmsSkipCount}건은 mWMS 취소를 건너뛰도록 판정해요` : ''}
      </div>
      <InputProblems parsed={parsed} />
      <div>
        <Button disabled={!isJudgeableInput(parsed) || requestInFlight} onClick={flow.judge}>
          판정 (dryRun)
        </Button>
      </div>
    </Stack>
  )
}
